"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { CreateSalonAppointmentFormSchema } from "./schema";
import { z } from "zod";
import { Form, FormLabel, FormMessage } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { UserJWTDecoded } from "@/types";
import hooks from "@/hooks";
import { useMemo } from "react";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { ICreateSalonAppointmentAction } from "@/actions/owner/types";
import PhoneInput from "react-phone-input-2";
import { ptBRCountries } from "@/consts/PtBRCountries";

type SalonAppointment = z.infer<typeof CreateSalonAppointmentFormSchema>;

interface ICreateSalonAppointmentForm {
  user: UserJWTDecoded["user"];
  closeModal: (isOpen: boolean) => void;
  handleAfterCreateAppointment: (
    apt: ICreateSalonAppointmentAction["Response"],
  ) => void;
}

const CreateSalonAppointmentForm = ({
  user,
  closeModal,
  handleAfterCreateAppointment,
}: ICreateSalonAppointmentForm) => {
  const t = useTranslations("Calendar");
  const { toast } = useToast();

  const form = useForm<SalonAppointment>({
    resolver: zodResolver(CreateSalonAppointmentFormSchema),
    defaultValues: {
      clientNumber: "+55",
      datetime: undefined,
      employee: "",
    },
  });
  const datetimeValue = form.watch("datetime");
  const selectedEmployee = form.watch("employee");

  const {
    data: salonServices,
    isLoading: isLoadingSalonServices,
    isError: isErrorSalonServices,
  } = hooks.useGetSalonServicesByEmployeeId(selectedEmployee);

  const {
    data: salonEmployees,
    isLoading: isLoadingSalonEmployees,
    isError: isErrorSalonEmployees,
  } = hooks.useGetSalonEmployeesByOwnerId(user.userid);

  const salonAppointmentService = hooks.useCreateSalonAppointment({
    onSuccess: (data) => {
      toast({
        title: "Sucesso",
        description: "Agendamento criado com sucesso",
        variant: "success",
      });
      handleAfterCreateAppointment(data);
      closeModal(false);
    },
    onError: (error) => {
      toast({
        title: "Não foi possível concluir o agendamento",
        description:
          error?.message ||
          "O horário pode já estar ocupado ou houve um problema momentâneo. Tente novamente.",
        variant: "warning",
        duration: 10000,
      });
    },
  });

  async function onSubmit(data: SalonAppointment) {
    await salonAppointmentService.mutateAsync({
      client_phone: data.clientNumber,
      date: data.datetime.toISOString(),
      employee_id: data.employee,
      salon_owner_id: user.userid,
      serviceName: data.service,
    });
  }

  const salonServicesOptions = useMemo(() => {
    if (isLoadingSalonServices || isErrorSalonServices) {
      return [];
    }
    if (!salonServices) return [];
    return salonServices.map((service) => ({
      label: service.name,
      value: service.name,
    }));
  }, [salonServices, isLoadingSalonServices, isErrorSalonServices]);

  const salonEmployeeOptions = useMemo(() => {
    if (isLoadingSalonEmployees || isErrorSalonEmployees) {
      return [];
    }
    if (!salonEmployees) return [];
    return salonEmployees.map((service) => ({
      label: service.name,
      value: service.id,
    }));
  }, [salonEmployees, isLoadingSalonEmployees, isErrorSalonEmployees]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="p-8 pl-0 pr-0 md:pl-8 md:pr-8 flex flex-col items-center gap-y-6"
      >
        <div className=" grid grid-cols-1 gap-4">
          <div className="flex flex-col ">
            <label htmlFor="whatsapp" className="text-sm font-semibold mb-1">
              {t("appointment.createAppointmentModal.form.labels.clientPhone")}
            </label>
            <PhoneInput
              country={"br"}
              value={form.watch("clientNumber")}
              onChange={(value) => {
                const numericValue = value.replace(/\D/g, "");
                form.setValue("clientNumber", numericValue);
              }}
              enableSearch={true}
              searchPlaceholder="Buscar"
              countryCodeEditable={false}
              localization={{ ...ptBRCountries }}
              inputStyle={{
                width: "100%",
                height: "40px",
                borderRadius: "9999px",
                border: "1px solid #e5e7eb",
                backgroundColor: "transparent",
                padding: "0.25rem 0.75rem",
                paddingLeft: "55px",
                fontSize: "1rem",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                transition: "all 0.2s",
                outline: "none",
              }}
              containerStyle={{
                width: "100%",
              }}
              buttonStyle={{
                borderTopLeftRadius: "9999px",
                borderBottomLeftRadius: "9999px",
                width: "50px",
                border: "1px solid rgb(229, 231, 235)",
                paddingLeft: "6px",
              }}
              dropdownStyle={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
              inputProps={{
                name: "whatsapp",
                placeholder: t(
                  "appointment.createAppointmentModal.form.placeholder.clientPhone",
                ),
                required: true,
              }}
            />
            {form.formState.errors.clientNumber && (
              <FormMessage>
                {form.formState.errors.clientNumber.message}
              </FormMessage>
            )}
          </div>

          <FormFields.SelectFormField
            form={form}
            formName="employee"
            label={t("appointment.createAppointmentModal.form.labels.employee")}
            placeholder={t(
              "appointment.createAppointmentModal.form.placeholder.employee",
            )}
            options={salonEmployeeOptions}
          />

          <FormFields.SelectFormField
            label={t("appointment.createAppointmentModal.form.labels.service")}
            form={form}
            formName="service"
            placeholder={t(
              "appointment.createAppointmentModal.form.placeholder.service",
            )}
            options={salonServicesOptions}
            isDisabled={!selectedEmployee}
          />

          {!selectedEmployee && (
            <FormMessage>
              Selecione um colaborador para visualizar os serviços disponíveis.
            </FormMessage>
          )}

          <div>
            <FormLabel>
              {t("appointment.createAppointmentModal.form.labels.datetime")}
            </FormLabel>
            <DateTimePicker
              value={datetimeValue}
              placeholder={t(
                "appointment.createAppointmentModal.form.placeholder.datetime",
              )}
              onChange={(e) => form.setValue("datetime", e as Date)}
              locale={ptBR}
            />
            {form.formState.errors.datetime && (
              <FormMessage>
                {form.formState.errors.datetime.message}
              </FormMessage>
            )}
          </div>
        </div>
        <div className="w-full space-y-2">
          <Buttons.DefaultButton
            className="py-6"
            type="submit"
            title={t("appointment.createAppointmentModal.confirmButton")}
          />
          <Buttons.DefaultButton
            variant="secondary"
            className="py-6"
            type="button"
            onClick={() => closeModal(false)}
            title={t("appointment.createAppointmentModal.closeButton")}
          />
        </div>
      </form>
    </Form>
  );
};

export default CreateSalonAppointmentForm;
